import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import http from '../../http-common';

const BudgetDetail = () => {
  const { t } = useTranslation();
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sendDialog, setSendDialog] = useState({ open: false });
  const [emailMessage, setEmailMessage] = useState('');
  const [sendRecipients, setSendRecipients] = useState([{ id: Date.now(), email: '' }]);
  const [storeEmails, setStoreEmails] = useState(true);
  const [updateStatusToSent, setUpdateStatusToSent] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    fetchBudget();
  }, [id]);

  const fetchBudget = async () => {
    setLoading(true);
    try {
      const response = await http.get(`/budgets/${id}`);
      setBudget(response.data.data);
    } catch (error) {
      showSnackbar('Error loading budget', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await http.delete(`/budgets/${id}`);
      showSnackbar('Budget deleted successfully');
      navigate('/budgets');
    } catch (error) {
      showSnackbar('Error deleting budget', 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await http.get(`/budgets/${id}/export/${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `budget_${id}.${type === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showSnackbar(`${type.toUpperCase()} exported successfully`);
    } catch (error) {
      showSnackbar(`Error exporting ${type.toUpperCase()}`, 'error');
    }
  };

  const openSendDialog = () => {
    const recipients = budget.recipients || [];
    if (recipients.length > 0) {
      setSendRecipients(recipients.map((recipient) => ({ id: recipient.id, email: recipient.email })));
    } else {
      setSendRecipients([{ id: Date.now(), email: '' }]);
    }
    setEmailMessage('');
    setStoreEmails(true);
    setSendDialog({ open: true });
  };

  const addSendRecipient = () => {
    setSendRecipients(prev => [...prev, { id: Date.now(), email: '' }]);
  };

  const updateSendRecipient = (recipientId, value) => {
    setSendRecipients(prev => prev.map(recipient => recipient.id === recipientId ? { ...recipient, email: value } : recipient));
  };

  const removeSendRecipient = (recipientId) => {
    setSendRecipients(prev => {
      if (prev.length === 1) return prev;
      return prev.filter(recipient => recipient.id !== recipientId);
    });
  };

  const confirmSend = async () => {
    try {
      const emails = sendRecipients
        .map(recipient => recipient.email?.trim())
        .filter(email => email);

      if (emails.length === 0) {
        showSnackbar('Please provide at least one email address', 'error');
        return;
      }

      await http.post(`/budgets/${id}/send-email`, {
        emails,
        message: emailMessage,
        store_emails: storeEmails,
        update_status_to_sent: updateStatusToSent,
      });

      showSnackbar('Budget sent successfully');
      setSendDialog({ open: false });
      fetchBudget();
    } catch (error) {
      showSnackbar('Error sending budget', 'error');
    }
  };

  const handleDuplicate = () => {
    // For now, just navigate to edit with a copy flag
    // In a real implementation, you'd call a duplicate API endpoint
    navigate(`/budgets/${id}/edit?duplicate=true`);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      sent: 'primary',
      approved: 'success',
      rejected: 'error'
    };
    return colors[status] || 'default';
  };

  const formatCurrency = (value) => {
    const { formatCurrencySync } = require('../../utils/currencyUtils');
    return formatCurrencySync(value);
  };

  if (loading || !budget) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => navigate('/budgets')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">{budget.name}</Typography>
            </Box>
          }
          action={
            <Box display="flex" gap={1}>
              <Tooltip title="Edit">
                <IconButton onClick={() => navigate(`/budgets/${id}/edit`)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Duplicate">
                <IconButton onClick={handleDuplicate}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send Budget">
                <IconButton onClick={openSendDialog}>
                  <PdfIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export PDF">
                <IconButton onClick={() => handleExport('pdf')}>
                  <PdfIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Excel">
                <IconButton onClick={() => handleExport('excel')}>
                  <ExcelIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton color="error" onClick={handleDelete}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Budget Information
                  </Typography>
                  <Typography><strong>Name:</strong> {budget.name}</Typography>
                  {budget.description && (
                    <Typography><strong>Description:</strong> {budget.description}</Typography>
                  )}
                  <Typography>
                    <strong>Status:</strong>{' '}
                    <Chip
                      label={budget.status.toUpperCase()}
                      color={getStatusColor(budget.status)}
                      size="small"
                    />
                  </Typography>
                  <Typography>
                    <strong>Created:</strong> {new Date(budget.created_at).toLocaleDateString()}
                  </Typography>
                  {budget.approved_at && (
                    <Typography>
                      <strong>Approved On:</strong> {new Date(budget.approved_at).toLocaleDateString()}
                    </Typography>
                  )}
                  {budget.valid_until && (
                    <Typography>
                      <strong>Valid Until:</strong> {new Date(budget.valid_until).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Client Information
                  </Typography>
                  {budget.client ? (
                    <>
                      <Typography><strong>Name:</strong> {budget.client.name}</Typography>
                      {budget.client.email && (
                        <Typography><strong>Email:</strong> {budget.client.email}</Typography>
                      )}
                      {budget.client.phone && (
                        <Typography><strong>Phone:</strong> {budget.client.phone}</Typography>
                      )}
                    </>
                  ) : (
                    <Typography color="textSecondary">
                      <em>No client associated</em>
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Financial Summary
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6">Total:</Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(budget.total)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Email Recipients
                  </Typography>
                  {(budget.recipients && budget.recipients.length > 0) ? (
                    budget.recipients.map((recipient) => (
                      <Typography key={recipient.id}>{recipient.email}</Typography>
                    ))
                  ) : (
                    <Typography color="textSecondary" fontStyle="italic">
                      No recipients defined
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader title="Budget Items" />
            <CardContent>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Line #</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>Unit Price</TableCell>
                      <TableCell>Total Price</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {budget.budget_items?.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.line_number}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.budget_type?.name}</TableCell>
                        <TableCell>{parseFloat(item.quantity).toFixed(3)}</TableCell>
                        <TableCell>{item.budget_unit?.formatted_name}</TableCell>
                        <TableCell>{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell>{formatCurrency(item.total_price)}</TableCell>
                        <TableCell>{item.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {budget.budget_type_summaries && budget.budget_type_summaries.length > 0 && (
            <Card variant="outlined">
              <CardHeader title="Summary by Type" />
              <CardContent>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Total Quantity</TableCell>
                        <TableCell>Total Amount</TableCell>
                        <TableCell>Percentage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {budget.budget_type_summaries.map((summary) => {
                        const percentage = budget.total > 0 
                          ? ((summary.total_amount / budget.total) * 100).toFixed(2)
                          : '0.00';
                        return (
                          <TableRow key={summary.id}>
                            <TableCell>{summary.budget_type?.name}</TableCell>
                            <TableCell>{parseFloat(summary.total_quantity).toFixed(3)}</TableCell>
                            <TableCell>{formatCurrency(summary.total_amount)}</TableCell>
                            <TableCell>{percentage}%</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>Delete Budget</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this budget?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sendDialog.open}
        onClose={() => setSendDialog({ open: false })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('budgets.sendBudgetViaEmail')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {t('budgets.sendDialogDescription')}
          </Typography>
          {sendRecipients.map((recipient, index) => (
            <Box key={recipient.id} display="flex" gap={1} alignItems="center" mt={1}>
              <TextField
                fullWidth
                label={t('budgets.emailNumber', { n: index + 1 })}
                value={recipient.email}
                onChange={(e) => updateSendRecipient(recipient.id, e.target.value)}
                placeholder={t('budgets.emailPlaceholder')}
              />
              <Button
                color="error"
                variant="outlined"
                onClick={() => removeSendRecipient(recipient.id)}
                disabled={sendRecipients.length === 1}
              >
                {t('budgets.remove')}
              </Button>
            </Box>
          ))}
          <Box mt={1}>
            <Button variant="text" onClick={addSendRecipient}>
              {t('budgets.addRecipient')}
            </Button>
          </Box>
          <TextField
            fullWidth
            label={t('budgets.messageOptional')}
            helperText={t('budgets.emailTemplateHint')}
            multiline
            rows={3}
            value={emailMessage}
            onChange={(e) => setEmailMessage(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Box mt={2}>
            <label>
              <input
                type="checkbox"
                checked={storeEmails}
                onChange={(e) => setStoreEmails(e.target.checked)}
              />
              {' '}{t('budgets.storeNewEmails')}
            </label>
          </Box>
          <Box mt={1}>
            <label>
              <input
                type="checkbox"
                checked={updateStatusToSent}
                onChange={(e) => setUpdateStatusToSent(e.target.checked)}
              />
              {' '}{t('budgets.updateStatusToSent')}
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialog({ open: false })}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={confirmSend}>{t('budgets.send')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BudgetDetail;